import type { ICalculationDefinition } from './types.js';

/**
 * The eight canonical flow-metric definitions, authored verbatim from the epic.
 *
 * Consolidated into a single ordered array (instead of one file per concept) so
 * the per-file structural boilerplate (`import type`, `const X: ICalculationDefinition = {…}`,
 * `export default`) is not repeated eight times.
 */

const FLOW_ISSUE_TYPES: ReadonlyArray<string> = ['Story', 'Bug', 'Task'] as const;

/**
 * Shared `source` block used by both Avg WIP Age and WIP Aging Distribution —
 * both join the daily snapshot to `raw_fact_issue_current` for the age source
 * and apply the same canonical WIP filter.
 */
const SNAPSHOT_WITH_AGE_JOIN_SOURCE = {
  primaryTable: 'raw_fact_issue_in_progress_daily',
  joins: [
    'raw_fact_issue_current ON raw_fact_issue_current.issue_key = raw_fact_issue_in_progress_daily.issue_key',
  ],
  sqlFilter:
    "archived = false AND in_progress_flag = true AND issue_type IN ('Story','Bug','Task') AND snapshot_date = :asOfDate",
} as const;

/**
 * Shared `scope` block for the snapshot-driven WIP concepts that join the age
 * source — same issue-type whitelist and same as-of-date rule as Work In Progress.
 */
const SNAPSHOT_WITH_AGE_JOIN_SCOPE = {
  issueTypes: FLOW_ISSUE_TYPES,
  archivedExcluded: true,
  asOfDateRule: 'min(today, period.to ?? today) — same as Work In Progress.',
} as const;

const wipCurrent: ICalculationDefinition = {
  conceptKey: 'wip-current',
  displayName: 'Work In Progress',
  category: 'flow',
  shortDescription: 'Number of items currently in progress for the selected scope.',
  fullDescription: [
    'A point-in-time count of items considered "in progress" for the chosen scope (team, product, or portfolio).',
    '',
    'The count reads from the daily snapshot table `raw_fact_issue_in_progress_daily` filtered to the canonical issue-type whitelist (`Story`, `Bug`, `Task`).',
    'Sub-tasks, Epics, Spikes, Initiatives, New Demands, Incidents, Data Products and Features are deliberately excluded so that headline numbers across Skystone agree on what "in progress" means.',
    '',
    'When the user selects a historical period, `asOfDate = min(today, period.to)` and the count reflects the end of that period; otherwise it reflects "now."',
    '',
    'If the snapshot for `asOfDate` has not landed yet (for example today\'s daily sync has not run), the count falls back to the most recent prior snapshot rather than returning zero. This matches Power BI\'s natural "as of latest refresh" behaviour — PBI\'s WIP measure does not pin to a specific snapshot date either, it shows whatever the dashboard\'s data refresh provides.',
  ].join('\n'),
  source: {
    primaryTable: 'raw_fact_issue_in_progress_daily',
    sqlFilter:
      "archived = false AND in_progress_flag = true AND issue_type IN ('Story','Bug','Task') AND snapshot_date = (SELECT MAX(snapshot_date) FROM raw_fact_issue_in_progress_daily WHERE snapshot_date <= :asOfDate)",
  },
  scope: {
    issueTypes: FLOW_ISSUE_TYPES,
    archivedExcluded: true,
    asOfDateRule: 'min(today, period.to ?? today) — computed inside the repository method, never passed in by the caller.',
  },
  pbiComparison: null,
  caveats: [
    'Sub-task / Subtask spelling variants exist in the source data; both are excluded by the issue-type whitelist.',
    'When the snapshot for `asOfDate` has not landed yet, the query resolves `snapshot_date` to the most recent prior snapshot ≤ `asOfDate` rather than returning zero. The reported value can therefore be from a date earlier than `asOfDate` if the daily sync is behind.',
  ],
  consumers: [
    'EpicBreakdownCard',
    'QualityRiskTab TrendSignals',
    'RepositoryDetailsPage KPI',
  ],
  repositoryMethod: 'getCurrentWipCount',
};

const wipTrend: ICalculationDefinition = {
  conceptKey: 'wip-trend',
  displayName: 'WIP Trend',
  category: 'flow',
  shortDescription: 'Daily (or weekly-bucketed) count of in-progress items across a chosen window.',
  fullDescription: [
    'A time series of the WIP count, one point per day in the selected window.',
    'Optionally aggregated to weekly buckets when callers request it.',
    '',
    'The series reads the same canonical filter as `Work In Progress`: the daily snapshot rows in `raw_fact_issue_in_progress_daily` for `Story / Bug / Task` issue types only, with `archived = false` and `in_progress_flag = true`.',
    '',
    'Returned rows are denormalised per `(date, issue_type, team_id)` (PRODOPSAI-418 shape extension) so consumers can render per-team breakdowns. Consumers that want a single daily total per `(date, type)` — including the existing weekly-bucketed chart — must first SUM across the team dimension. See PRODOPSAI-422 for the aggregation invariant.',
    '',
    'Invariant (US4): `getWipTrend(scope, asOfDate, asOfDate, "daily").last() = getCurrentWipCount(scope, asOfDate)` after summing across `team_id`.',
  ].join('\n'),
  source: {
    primaryTable: 'raw_fact_issue_in_progress_daily',
    sqlFilter:
      "archived = false AND in_progress_flag = true AND issue_type IN ('Story','Bug','Task') AND snapshot_date BETWEEN :from AND :asOfDate GROUP BY snapshot_date, issue_type, team_id",
  },
  scope: {
    issueTypes: FLOW_ISSUE_TYPES,
    archivedExcluded: true,
    asOfDateRule: 'min(today, period.to ?? today) — same as Work In Progress.',
  },
  pbiComparison: null,
  caveats: [
    'Sub-task / Subtask spelling variants exist in the source data; both are excluded by the issue-type whitelist.',
    'Result set is one row per `(date, issue_type, team_id)` since PRODOPSAI-418 — consumers that need a single daily total must SUM across the team dimension before any further bucketing (PRODOPSAI-422).',
  ],
  consumers: [
    'metric-view WipTrendCard',
    'metric-view WipSummaryCard',
    'TeamDashboardPage InProgressByTypeChart',
  ],
  repositoryMethod: 'getWipTrend',
};

const wipAvgAge: ICalculationDefinition = {
  conceptKey: 'wip-avg-age',
  displayName: 'Avg WIP Age',
  category: 'flow',
  shortDescription: 'Mean age in days of currently in-progress items, measured from `first_in_progress_date`.',
  fullDescription: [
    'Mean of `asOfDate − first_in_progress_date` over items currently in WIP at the snapshot. Returns `{ avgDays, coverageCount, totalCount }` — `coverageCount` counts WIP items with `first_in_progress_date IS NOT NULL`; `totalCount` is the full WIP count. When `coverageCount < totalCount`, the average is partial and consumers should render a confidence indicator.',
    '',
    'The age source is `raw_fact_issue_current.first_in_progress_date`, joined to the daily WIP snapshot. The column `days_in_progress` is **not** used as a filter or as the age source — it represents cycle-time-on-completion in the source schema and is NULL for ~97% of currently-in-progress issues (US1 finding).',
    '',
    'The repository implementation (US3) computes `totalCount` over the unfiltered WIP set and `coverageCount` over the subset with non-null `first_in_progress_date`.',
  ].join('\n'),
  source: SNAPSHOT_WITH_AGE_JOIN_SOURCE,
  scope: SNAPSHOT_WITH_AGE_JOIN_SCOPE,
  pbiComparison: null,
  caveats: [
    '`days_in_progress` is NULL for ~97% of currently-in-progress issues (US1 finding) — never use it as an age filter.',
    'Items without a `first_in_progress_date` are excluded from the mean. Render `coverageCount` vs `totalCount` to indicate confidence.',
  ],
  consumers: [
    'DeliveryTab WIP Aging',
  ],
  repositoryMethod: 'getCurrentWipAvgAge',
};

const wipAging: ICalculationDefinition = {
  conceptKey: 'wip-aging',
  displayName: 'WIP Aging Distribution',
  category: 'flow',
  shortDescription: 'Per-product bucket counts of WIP items grouped by age: `<7d / 7-14d / 14-30d / >30d`.',
  fullDescription: [
    'Distribution of currently-in-progress items into four age buckets:',
    '',
    '- `<7d` — newer than seven days',
    '- `7-14d` — between seven and fourteen days',
    '- `14-30d` — between fourteen and thirty days',
    '- `>30d` — older than thirty days',
    '',
    'Bucketing key is `today − first_in_progress_date`. Items without a `first_in_progress_date` are excluded from bucketing (and counted in `unbucketedCount`), which is why `Σ buckets ≤ getCurrentWipCount` is enforced as an invariant in US4.',
  ].join('\n'),
  source: SNAPSHOT_WITH_AGE_JOIN_SOURCE,
  scope: SNAPSHOT_WITH_AGE_JOIN_SCOPE,
  pbiComparison: null,
  caveats: [
    'Items without a `first_in_progress_date` are unbucketed; `Σ buckets ≤ getCurrentWipCount` (invariant 3, US4).',
  ],
  consumers: [
    'DeliveryTab WIP Aging',
    'Insight tool get_wip_aging',
  ],
  repositoryMethod: 'getWipAgingDistribution',
};

const open: ICalculationDefinition = {
  conceptKey: 'open',
  displayName: 'Open Issues',
  category: 'flow',
  shortDescription: 'Items not yet Done and not Cancelled — Skystone-internal concept; no Power BI counterpart.',
  fullDescription: [
    'Count of issues that are still actionable: not Done, not Cancelled, not archived.',
    '',
    '"Open" is a strictly broader set than "Work In Progress" — it includes Backlog and `To Do` items in addition to those currently being worked on.',
    'Invariant (US4): `getCurrentWipCount(scope) ≤ getOpenIssueCount(scope) ≤ Total non-archived in scope`.',
    '',
    'Open is **not** a Power BI metric; the PBI document defines no equivalent. It exists so Skystone can show a single auditable number for "outstanding work" instead of letting individual cards reinvent the filter.',
  ].join('\n'),
  source: {
    primaryTable: 'raw_fact_issue_current',
    sqlFilter:
      "archived = false AND status_normalized NOT IN ('Done','Cancelled') AND issue_type IN ('Story','Bug','Task')",
  },
  scope: {
    issueTypes: FLOW_ISSUE_TYPES,
    archivedExcluded: true,
  },
  pbiComparison: {
    pbiBehavior: 'No equivalent metric in `PE_Dashboard_Visual_Definitions.docx`.',
    skystoneBehavior: 'Skystone-internal concept that excludes Done and Cancelled.',
    deviationReason:
      'Skystone needs a single auditable "outstanding work" filter so multiple cards do not invent their own. PBI never modelled the concept.',
  },
  caveats: [
    '`status_normalized` may be stale — multiple rows observed with `issue_status = "Done"` but `status_normalized = "In Progress"`. Root cause owned by US1.',
  ],
  consumers: [
    'RepositoryDetailsPage KPI (pending Q4 confirmation)',
  ],
  repositoryMethod: 'getOpenIssueCount',
};

const total: ICalculationDefinition = {
  conceptKey: 'total',
  displayName: 'Total Issues',
  category: 'flow',
  shortDescription: 'All non-archived issues in scope — Power BI parity reference.',
  fullDescription: [
    'Count of every non-archived issue of the canonical issue types in scope, regardless of status (includes Done, Cancelled, In Progress, Backlog, etc.).',
    '',
    'Used as the upper bound for the open / WIP invariants (US4): `getCurrentWipCount ≤ getOpenIssueCount ≤ Total non-archived in scope`.',
    '',
    '`Total` does **not** have a single canonical repository method — individual cards run ad-hoc queries against `raw_fact_issue_current`. The Calculation Reference documents the canonical filter so future ad-hoc queries can be checked for parity.',
  ].join('\n'),
  source: {
    primaryTable: 'raw_fact_issue_current',
    sqlFilter: "archived = false AND issue_type IN ('Story','Bug','Task')",
  },
  scope: {
    issueTypes: FLOW_ISSUE_TYPES,
    archivedExcluded: true,
  },
  pbiComparison: null,
  caveats: [
    'No single repository method backs this concept. Each consumer issues an ad-hoc query — verify the filter matches `archived = false AND issue_type IN ("Story","Bug","Task")`.',
  ],
  consumers: [
    'RepositoryDetailsPage (legacy `issueCurrentApi.getSummary`, pending Q4 migration)',
  ],
  repositoryMethod: null,
};

const cancelled: ICalculationDefinition = {
  conceptKey: 'cancelled',
  displayName: 'Cancelled',
  category: 'flow',
  shortDescription: 'Items closed as Cancelled / Won’t Do — separated from Open so the two are never conflated.',
  fullDescription: [
    'Count of issues whose `status_normalized = "Cancelled"`.',
    '',
    'Separated explicitly from `Open` because lumping Cancelled into "outstanding" would inflate any backlog burndown. Cancelled is also separated from Done because the work was not actually completed.',
    '',
    'No single canonical repository method backs Cancelled — consumers query `raw_fact_issue_current` ad-hoc. The Calculation Reference documents the canonical filter so the same definition is reused everywhere.',
  ].join('\n'),
  source: {
    primaryTable: 'raw_fact_issue_current',
    sqlFilter:
      "archived = false AND status_normalized = 'Cancelled' AND issue_type IN ('Story','Bug','Task')",
  },
  scope: {
    issueTypes: FLOW_ISSUE_TYPES,
    archivedExcluded: true,
  },
  pbiComparison: null,
  caveats: [
    '`status_normalized` may be stale (US1 finding); a row may show `status_normalized = "Cancelled"` even after a status change to a non-cancelled value.',
  ],
  consumers: [
    'Reference-only — no current UI consumer',
  ],
  repositoryMethod: null,
};

const throughput: ICalculationDefinition = {
  conceptKey: 'throughput',
  displayName: 'Throughput',
  category: 'flow',
  shortDescription: 'Count of issues that moved to Done in the selected window — Power BI parity reference.',
  fullDescription: [
    'Number of issues whose `last_done_date` falls inside the selected window.',
    '',
    'Throughput is documented here as a Power BI parity reference. The existing throughput methods in `portfolio-repository.ts` already produce the correct number; this epic does **not** introduce a new canonical method for throughput. A future epic on velocity / DORA parity may consolidate throughput callers.',
    '',
    '`is_done = true` plus `last_done_date BETWEEN window.from AND window.to` is the canonical filter; archived rows are excluded.',
  ].join('\n'),
  source: {
    primaryTable: 'raw_fact_issue_current',
    sqlFilter:
      "archived = false AND is_done = true AND last_done_date BETWEEN :from AND :to AND issue_type IN ('Story','Bug','Task')",
  },
  scope: {
    issueTypes: FLOW_ISSUE_TYPES,
    archivedExcluded: true,
    asOfDateRule: 'Window-based; throughput is a count over a window, not a point-in-time number.',
  },
  pbiComparison: null,
  caveats: [
    'Power BI documents weekly bucketing for Lead Time / DF; Skystone callers may bucket differently — see the PBI Comparison page for cross-cutting deviations.',
  ],
  consumers: [
    'Reference-only — existing throughput callers untouched in this epic',
  ],
  repositoryMethod: null,
};

const statusCount: ICalculationDefinition = {
  conceptKey: 'status-count',
  displayName: 'Status Count',
  category: 'governance',
  shortDescription:
    'Number of distinct statuses observed on in-scope items at any moment during the selected window.',
  fullDescription: [
    'A count of distinct statuses that any in-scope item was in at any moment during the selected window — both items that transitioned through a status and items that sat in a status across the window.',
    '',
    'Implemented as a per-issue segment reconstruction over `jira_status_changes`, joined to `jira_issues` for issue scope and to `raw_fact_issue_current` for team / product enrichment. A status counts for the window `[start, end)` iff there exists a `(status_name, entered_at, exited_at)` segment on any in-scope issue such that `entered_at < end AND COALESCE(exited_at, "infinity") > start`. This is the Part A + B period semantic from the epic.',
    '',
    'Segments are derived from `jira_status_changes` ordered by `changed_at` per issue: each `(issue_id, to_status)` row starts at its `changed_at` and ends at the next row\'s `changed_at` (or `+infinity` if it is the latest row). The earliest row\'s `from_status` defines an initial segment with `entered_at = -infinity`. If an issue has no rows in `jira_status_changes`, its `current_status` is treated as a single `(-infinity, +infinity)` segment.',
    '',
    'In-scope issues are determined by joining `jira_issues.issue_key = raw_fact_issue_current.issue_key` so the Fabric-known team / product filters used by every other workflow-hygiene card are inherited verbatim. Sub-tasks are included unconditionally — no issue-type filter.',
    '',
    'Status names are collapsed case-insensitively before counting, and statuses outside the org canonical spine (`To Do`, `In Progress`, `In Review`, `Done`) are reported separately as non-canonical.',
    '',
    'Implementing methods (live on `JiraStatusHistoryRepository`): `getStatusCountForWindow` drives the metric-view Workflow Hygiene card, `getStatusCountPerPeriod` drives the Quality Control matrix card. The `repositoryMethod` field below is left `null` because the `ICalculationDefinition` shape supports only a single method name and Status Count has two — the names are documented in this description instead.',
  ].join('\n'),
  source: {
    primaryTable: 'jira_issues',
    joins: [
      'jira_status_changes ON jira_status_changes.issue_id = jira_issues.id',
      'raw_fact_issue_current ON raw_fact_issue_current.issue_key = jira_issues.issue_key',
    ],
    sqlFilter:
      "raw_fact_issue_current.archived = false AND segment.entered_at < :to AND COALESCE(segment.exited_at, 'infinity') > :from",
  },
  scope: {
    archivedExcluded: true,
    asOfDateRule:
      'Window-based; Status Count is a distinct count of statuses observed during the window `[from, to)`, not a point-in-time number.',
  },
  pbiComparison: null,
  caveats: [
    'Status names are compared case-insensitively; `"In progress"` and `"IN PROGRESS"` collapse to the same status before counting.',
    'Issues that exist in `jira_issues` but not in `raw_fact_issue_current` are silently dropped, since the in-scope filter inherits Fabric team / product enrichment. Brand-new Jira projects not yet onboarded to Fabric therefore contribute nothing to the count.',
    'Sub-tasks are included unconditionally; the canonical issue-type whitelist used by WIP / Open / Throughput does not apply here.',
  ],
  consumers: ['StatusCountCard', 'QualityControlTab MatrixCard'],
  repositoryMethod: null,
};

/**
 * Shared `joins` block for snapshot-driven code-quality concepts. The
 * `quality_latest` read joins through the SonarCloud project dim, the
 * repository dim, and (optionally) the product / team scope filter.
 */
const SONAR_SNAPSHOT_JOINS: ReadonlyArray<string> = [
  'raw_dim_sonarqube_project sp ON sp.project_key = quality_latest.project_key',
  'raw_dim_repository r ON r.id = sp.repository_id (r.archived = false)',
  'raw_bridge_repository_product brp ON brp.repository_id = r.id (joined when productId or teamId is set)',
  'raw_dim_team t ON t.product_id = brp.product_id (joined when teamId is set; t.archived = false)',
] as const;

/**
 * Shared `joins` block for history-driven code-quality concepts. The
 * `quality_history` read joins through the SonarCloud project dim, the
 * repository dim, the bridge + product, and team (LEFT JOIN, optional filter).
 */
const SONAR_HISTORY_JOINS: ReadonlyArray<string> = [
  'raw_dim_sonarqube_project p ON p.project_key = h.project_key',
  'raw_dim_repository r ON r.id = p.repository_id (r.archived = false)',
  'raw_bridge_repository_product b ON b.repository_id = r.id',
  'raw_dim_product prod ON prod.id = b.product_id (prod.archived = false)',
  'raw_dim_team team ON team.product_id = prod.id (LEFT JOIN; team.archived = false; optional teamId filter)',
] as const;

// ---------------------------------------------------------------------------
// Code-quality concepts (PRODOPSAI-441).
//
// Audience: Skystone data-quality admins. They do not have SonarCloud access,
// so every SonarCloud concept is defined inline on first use. Prose is
// deliberately self-contained — an admin reading one card must have enough
// context to answer the user's question without leaving the page.
//
// Authored via the `defineQualityConcept` factory below so per-card content
// stays content-only and the shared scope / category / pbi-comparison fields
// live in one place.
// ---------------------------------------------------------------------------

interface IQualityConceptArgs {
  readonly conceptKey: string;
  readonly displayName: string;
  readonly shortDescription: string;
  /** Paragraphs joined with `\n` to produce the markdown `fullDescription`. */
  readonly fullDescription: ReadonlyArray<string>;
  readonly source: ICalculationDefinition['source'];
  readonly asOfDateRule: string;
  /** Defaults to `true` — only cq-sync-caveats needs `false`. */
  readonly archivedExcluded?: boolean;
  readonly caveats?: ReadonlyArray<string>;
  readonly consumers: ReadonlyArray<string>;
  readonly repositoryMethod: string;
}

function defineQualityConcept(args: IQualityConceptArgs): ICalculationDefinition {
  return {
    conceptKey: args.conceptKey,
    displayName: args.displayName,
    category: 'quality',
    shortDescription: args.shortDescription,
    fullDescription: args.fullDescription.join('\n'),
    source: args.source,
    scope: {
      archivedExcluded: args.archivedExcluded ?? true,
      asOfDateRule: args.asOfDateRule,
    },
    pbiComparison: null,
    caveats: args.caveats,
    consumers: args.consumers,
    repositoryMethod: args.repositoryMethod,
  };
}

/** Helper for the seven concepts that read `quality_latest` via SONAR_SNAPSHOT_JOINS. */
function snapshotSource(sqlFilter: string): ICalculationDefinition['source'] {
  return {
    primaryTable: 'raw_fact_sonarqube_project_quality_latest',
    joins: SONAR_SNAPSHOT_JOINS,
    sqlFilter,
  };
}

/** Helper for the three trend concepts that read `quality_history` via SONAR_HISTORY_JOINS. */
function historySource(sqlFilter: string): ICalculationDefinition['source'] {
  return {
    primaryTable: 'raw_fact_sonarqube_project_quality_history',
    joins: SONAR_HISTORY_JOINS,
    sqlFilter,
  };
}

const cqCoverageCurrent = defineQualityConcept({
  conceptKey: 'cq-coverage-current',
  displayName: 'Median Coverage',
  shortDescription: 'Median test coverage across in-scope repositories at their most recent code analysis.',
  fullDescription: [
    "A point-in-time view of test coverage across the portfolio: the median of each in-scope repository's most recent coverage reading.",
    '',
    'Coverage measures **how much of the executable code is exercised by automated tests** — typically a weighted percentage of statements and branches that ran during the test suite. The value comes from SonarCloud, which calculates it after every code-analysis scan. A reading of 80 means 80% of the executable code was covered; the remaining 20% was not exercised by tests.',
    '',
    "The cohort is every non-archived repository that has at least one SonarCloud project. Each repository contributes one value — the coverage percentage from its **latest** scan, read from the `quality_latest` snapshot table. Repositories with multiple SonarCloud projects (rare but possible, for example when a repo has multiple build modules) are deduplicated: only the project with the most recent analysis is counted.",
    '',
    "Median (not mean) is used so that one low-coverage repository does not drag the headline down. Repositories whose latest scan recorded no coverage value (for example, because the project is configured without test-coverage reporting) are excluded from the median but still counted in the 'in-scope repos' total surfaced elsewhere on the page.",
    '',
    'No time window is applied here. The `from` / `to` filter on the request controls the trend sparkline only; the snapshot tile always shows "as of the most recent ingestion".',
  ],
  source: snapshotSource(
    "r.archived = false AND sp.repository_id IS NOT NULL -- optional: brp.product_id = :productId AND t.id = :teamId. DISTINCT ON (r.id) ORDER BY last_analysis_at DESC NULLS LAST. Headline = MEDIAN(coverage_pct) across the resulting rows.",
  ),
  asOfDateRule: 'Latest available scan per repository — the request from/to window is ignored for the snapshot tile.',
  caveats: [
    'A repository whose latest scan was years ago still contributes that old coverage value — there is no freshness floor on this calculation. The Freshness footer at the bottom of the tab reports the cohort-wide newest scan date so consumers can judge confidence.',
    'When a repository has multiple SonarCloud projects, only the most recently analysed wins. The other project\'s coverage is invisible to this number.',
    'NULL coverage_pct values (repository scanned but no coverage report) are excluded from the median rather than treated as zero. A portfolio with mixed coverage-reporting practices may therefore have a smaller effective cohort than the in-scope repos count suggests.',
  ],
  consumers: ['Median Coverage tile (Code Quality tab, verdict strip)'],
  repositoryMethod: 'getRepoSnapshot',
});


const cqCoverageTrend = defineQualityConcept({
  conceptKey: 'cq-coverage-trend',
  displayName: 'Coverage Trend',
  shortDescription: 'Weekly median coverage across in-scope repositories, with last-observation-carried-forward so every week reflects the full cohort.',
  fullDescription: [
    'A time series of portfolio coverage, one Monday-aligned weekly bucket per ISO week in the selected window. Each bucket value is the median of the per-repository coverage readings **as of that week**.',
    '',
    "Source rows come from `raw_fact_sonarqube_project_quality_history` filtered to `metric_key = 'coverage'`. For each (week, project) pair, the SQL takes the average of `metric_value` so multiple PR-driven scans in the same week smooth out into a single per-project number for that week.",
    '',
    "Most repositories do not scan every week — a scan only runs when someone pushes code that triggers one. A naive median over each week would therefore reflect only that week's scanners, which is misleading: a quiet week with one outlier scan would crash the headline even when the rest of the portfolio is healthy.",
    '',
    "To fix that, the service applies **last-observation-carried-forward (LOCF)**: for each repository, weeks with no scan are filled with that repository's most recent prior coverage value. The carry-forward is seeded from pre-window scans too (a second query against `quality_history` with `metric_at < :from`), so repositories whose latest scan predates the requested window still contribute. There is **no time cap**: a long-dormant repository continues to be counted at its last-known coverage until a newer scan lands (PRODOPSAI-440 product call — surfacing a freshness caveat is a deliberate follow-up).",
    '',
    'When the selected window has no in-window scans at all but pre-window seeds exist (a fully quiet period), the helper synthesises Monday-aligned bucket starts spanning the requested range and fills them from the seed, so the chart still reflects the cohort during quiet periods rather than going empty.',
    '',
    "The headline number under the sparkline is the **most recent bucket's value**, not a period statistic. The `level` chip (high / medium / low / critical) classifies this value at coverage thresholds 80 / 60 / 30.",
  ],
  source: historySource(
    "h.metric_key = 'coverage' AND h.metric_at BETWEEN :from AND :to AND h.metric_value IS NOT NULL AND (:productId IS NULL OR prod.id = :productId) AND (:teamId IS NULL OR team.id = :teamId) GROUP BY DATE_TRUNC('week', h.metric_at AT TIME ZONE 'UTC')::date, p.project_key -- AVG(metric_value) per (week, project); the median across projects per bucket is computed in the service. Pre-window seed query: same join chain, metric_at < :from, DISTINCT ON (p.project_key) ORDER BY p.project_key, h.metric_at DESC.",
  ),
  asOfDateRule: 'Bucket starts are ISO Monday weeks aligned via DATE_TRUNC. The headline reflects the latest bucket in the requested window.',
  caveats: [
    "The headline is the latest bucket's median, not a median across the period. A date range whose most recent bucket is a sparse week will tilt the headline toward that bucket (mitigated by LOCF, but the chosen bucket still drives the number).",
    'The reliability and maintainability trend axes do NOT use LOCF — their reducer is bounded and not cohort-sensitive in the same way (see *Reliability Trend* / *Maintainability Trend*).',
  ],
  consumers: ['Coverage sparkline (Code Quality tab, trend column)'],
  repositoryMethod: 'getTrendSeries',
});

const cqQgPassRate = defineQualityConcept({
  conceptKey: 'cq-qg-pass-rate',
  displayName: 'Quality Gate Pass Rate',
  shortDescription: 'Share of in-scope repositories whose latest code-analysis scan passed the configured quality gate.',
  fullDescription: [
    'The percentage of in-scope repositories whose **most recent code-analysis scan passed** the quality gate configured for that repository.',
    '',
    'A **quality gate** is SonarCloud\'s pass/fail check applied at the end of every scan. It is a configurable set of conditions on the new code introduced in the scan — typical conditions include minimum coverage on new code, maximum duplication, and minimum reliability/security ratings. If every condition passes, the scan is recorded as `PASSED`; if any fails, the scan is recorded as `FAILED`. The quality gate is per-repository in principle, but in practice the org tends to use the same gate configuration across most repositories.',
    '',
    'The headline is `qgPassed / qgTotal` where:',
    '',
    "- `qgPassed` counts repositories whose latest scan's `quality_status` normalises to `PASSED`.",
    '- `qgTotal` counts repositories whose latest scan has any quality-gate verdict at all (`PASSED`, `OK`, `FAILED`, `ERROR` — the codes are normalised: `PASSED` + `OK` → PASSED, `FAILED` + `ERROR` → FAILED). Repositories with no quality-gate verdict (`quality_status IS NULL`) are excluded from the denominator.',
    '',
    "The cohort is every non-archived repository with at least one SonarCloud project, deduplicated to one project per repository (most recent analysis wins). The headline is computed in the service layer from the same snapshot read used by the coverage and reliability tiles.",
  ],
  source: snapshotSource(
    "r.archived = false AND sp.repository_id IS NOT NULL -- optional: brp.product_id = :productId AND t.id = :teamId. DISTINCT ON (r.id) ORDER BY last_analysis_at DESC NULLS LAST. Service normalises quality_status (OK→PASSED, ERROR→FAILED, other→null) and computes pass count / total.",
  ),
  asOfDateRule: 'Latest available scan per repository — the request from/to window is ignored for the snapshot tile.',
  caveats: [
    'Repositories whose latest scan has `quality_status IS NULL` (no quality-gate verdict recorded — most likely a misconfigured project) are excluded from BOTH numerator and denominator. The tile sub-label shows the denominator so consumers can spot a shrunken cohort.',
    'Quality-gate conditions are configured per-repository in SonarCloud. Two repositories with the same coverage can have different pass/fail outcomes if their gates impose different thresholds. The tile reports SonarCloud\'s verdict, not a recomputed verdict against a Skystone-defined gate.',
  ],
  consumers: ['Quality Gate Pass Rate tile (Code Quality tab, verdict strip)'],
  repositoryMethod: 'getRepoSnapshot',
});

const cqReliabilityCurrent = defineQualityConcept({
  conceptKey: 'cq-reliability-current',
  displayName: 'Reliability Grade Mix',
  shortDescription: 'Distribution of in-scope repositories across SonarCloud reliability grades A–E at their most recent scan.',
  fullDescription: [
    'A breakdown of how many in-scope repositories sit at each reliability grade (A best, E worst) at their most recent code-analysis scan.',
    '',
    "**Reliability** is SonarCloud's verdict on bug-risk: how many bugs of which severity the analyser found in the code. The grade summarises the per-repository bug list:",
    '',
    '- **A** — no bugs above MINOR severity.',
    '- **B** — at least one MINOR bug.',
    '- **C** — at least one MAJOR bug.',
    '- **D** — at least one CRITICAL bug.',
    '- **E** — at least one BLOCKER bug.',
    '',
    "These thresholds are SonarSource's defaults. Repositories whose latest scan recorded no reliability grade (project misconfigured or never analysed) appear in the tile as `nullCount` separately from grades A–E.",
    '',
    'The tile shows raw counts for each grade plus the total. The cohort is the same in-scope set as the rest of the verdict strip: non-archived repositories with at least one SonarCloud project, deduplicated to one project per repository (most recent analysis wins).',
    '',
    'The grade is stored as an uppercase letter (`A` / `B` / `C` / `D` / `E`) in `quality_latest.reliability_grade`. Skystone defensively narrows the value: anything that is not one of those five letters is treated as `null`.',
  ],
  source: snapshotSource(
    "r.archived = false AND sp.repository_id IS NOT NULL -- optional: brp.product_id = :productId AND t.id = :teamId. DISTINCT ON (r.id). Service groups the resulting rows by reliability_grade and emits {a, b, c, d, e, nullCount}.",
  ),
  asOfDateRule: 'Latest available scan per repository — the request from/to window is ignored for the snapshot tile.',
  caveats: [
    'The grade encodes the **worst-severity bug present**, not the bug count. A repository with one BLOCKER bug and 50 MINOR bugs receives grade E; a repository with 50 MINOR bugs and no higher-severity bugs receives grade B. The grade does not scale with how many bugs exist.',
  ],
  consumers: ['Reliability Grade Mix tile (Code Quality tab, verdict strip)'],
  repositoryMethod: 'getRepoSnapshot',
});

const cqReliabilityTrend = defineQualityConcept({
  conceptKey: 'cq-reliability-trend',
  displayName: 'Reliability Trend',
  shortDescription: 'Weekly share of in-scope repositories at reliability grade A.',
  fullDescription: [
    'A time series showing the percentage of in-scope repositories at reliability grade A in each ISO week of the selected window. A higher value means more of the portfolio is bug-clean (see *Reliability Grade Mix* for what each grade means).',
    '',
    "Source rows come from `raw_fact_sonarqube_project_quality_history` filtered to `metric_key = 'reliability_rating'`. Crucially, the history table stores the grade as an **integer 1–5** (1 = grade A, 5 = grade E) rather than a letter. This is a categorical scale, **not a numeric one** — averaging it would produce meaningless values like 2.4. For that reason:",
    '',
    '- The SQL takes the **latest** reading per (week, project) via `DISTINCT ON` — not an average.',
    '- The service computes the bucket value as **the percentage of in-scope repositories at value = 1 (grade A)** in that week — not a numeric aggregate.',
    '',
    "**LOCF is NOT applied to this axis.** The grade-A% reducer is bounded 0–100 and clusters near 100 when most repositories are bug-clean (most are), so the cohort-shift problem that motivated coverage's LOCF does not show up here in the same way. Adding LOCF to reliability would be a separate change.",
    '',
    "The headline number is the most recent bucket's percentage. The `level` chip thresholds are 80 / 50 / 20 percent at grade A.",
  ],
  source: historySource(
    "h.metric_key = 'reliability_rating' AND h.metric_at BETWEEN :from AND :to AND h.metric_value IS NOT NULL AND (:productId IS NULL OR prod.id = :productId) AND (:teamId IS NULL OR team.id = :teamId) -- DISTINCT ON (DATE_TRUNC('week', metric_at)::date, project_key) ORDER BY week, project_key, metric_at DESC. Service counts the percentage at value = 1 per bucket.",
  ),
  asOfDateRule: 'Bucket starts are ISO Monday weeks. The headline reflects the latest bucket in the requested window.',
  caveats: [
    'Rating is stored as integer 1–5 in the history table (1 = A, 5 = E). Do NOT average it.',
    'No LOCF — sparse weeks may show artificially low or high values if only a few repositories scanned, but the bounded reducer keeps the swings small in practice.',
  ],
  consumers: ['Reliability sparkline (Code Quality tab, trend column)'],
  repositoryMethod: 'getTrendSeries',
});

const cqMaintainabilityTrend = defineQualityConcept({
  conceptKey: 'cq-maintainability-trend',
  displayName: 'Maintainability Trend',
  shortDescription: 'Weekly share of in-scope repositories at maintainability grade A (low technical debt).',
  fullDescription: [
    'A time series showing the percentage of in-scope repositories at maintainability grade A in each ISO week of the selected window.',
    '',
    '**Maintainability** in SonarCloud is the technical-debt grade. SonarCloud estimates how long it would take a developer to fix every code smell in a project (the **technical debt**), then divides that by the time it would take to write the same code from scratch (the **development cost**). The ratio is the technical-debt ratio. The grade thresholds (SonarSource defaults) are:',
    '',
    '- **A** — debt ratio ≤ 5% of development cost.',
    '- **B** — 5–10%.',
    '- **C** — 10–20%.',
    '- **D** — 20–50%.',
    '- **E** — ≥ 50%.',
    '',
    'The SonarCloud column for this metric is `sqale_rating` — SQALE stands for *Software Quality Assessment based on Lifecycle Expectations*, the methodology Sonar uses. It is stored as the same integer 1–5 scale as reliability (1 = grade A, 5 = grade E).',
    '',
    'Aggregation is identical to *Reliability Trend*: `DISTINCT ON (week, project_key)` to take the latest grade per week per repository, then the service computes the percentage at value = 1 per bucket. No LOCF.',
    '',
    "The headline number is the most recent bucket's percentage. The `level` chip thresholds are 80 / 50 / 20 percent at grade A.",
  ],
  source: historySource(
    "h.metric_key = 'sqale_rating' AND h.metric_at BETWEEN :from AND :to AND h.metric_value IS NOT NULL AND (:productId IS NULL OR prod.id = :productId) AND (:teamId IS NULL OR team.id = :teamId) -- DISTINCT ON (week, project_key) ORDER BY week, project_key, metric_at DESC. Service counts the percentage at value = 1 per bucket.",
  ),
  asOfDateRule: 'Bucket starts are ISO Monday weeks. The headline reflects the latest bucket in the requested window.',
  caveats: [
    "The SonarCloud column is named `sqale_rating` (a methodology acronym), but the user-facing label is just 'Maintainability'. Don't be thrown by the column name — they refer to the same number.",
    'Rating is stored as integer 1–5; do NOT average. No LOCF.',
  ],
  consumers: ['Maintainability sparkline (Code Quality tab, trend column)'],
  repositoryMethod: 'getTrendSeries',
});

const cqByTeam = defineQualityConcept({
  conceptKey: 'cq-by-team',
  displayName: 'Code Quality by Team',
  shortDescription: 'Per-team coverage average and quality-gate pass count from the latest snapshot, deduplicated to one Sonar project per repository per team.',
  fullDescription: [
    'A team-level breakout of the snapshot data used by the Median Coverage and QG Pass Rate tiles, grouped by team rather than aggregated across the whole portfolio.',
    '',
    "Each team's row contains:",
    '',
    '- **Repo count** — distinct repositories owned by the team (via product membership).',
    "- **Weighted coverage** — average of `coverage_pct` across the team's repositories. Each repository contributes one value (its most recent scan); repositories with NULL coverage are excluded from this average.",
    '- **QG passed / total** — count of repositories whose latest scan is PASSED, over the count whose latest scan has any verdict at all (same normalisation as the QG Pass Rate tile).',
    '',
    "The team-to-repository link goes: `team → product → bridge → repository`. A repository can belong to multiple teams under the same product, in which case it contributes to each of those teams' counts. This double-count is deliberate (D14 decision in the original spec): the team breakouts answer 'which teams own this number', not 'which repositories exist exactly once'.",
    '',
    "Inside the SQL, a CTE deduplicates `(team_id, repository_id)` pairs by `last_analysis_at DESC` before aggregating, so a repository with multiple SonarCloud projects only contributes its most-recent project's data to each team.",
    '',
    'This calculation is **skipped** (returns empty) when a `teamId` filter is set on the request — single-team scope has no breakouts to show.',
  ],
  source: {
    primaryTable: 'raw_fact_sonarqube_project_quality_latest',
    joins: [
      'raw_dim_team t (t.archived = false)',
      'raw_dim_product prod ON prod.id = t.product_id (prod.archived = false)',
      'raw_bridge_repository_product b ON b.product_id = prod.id',
      'raw_dim_repository r ON r.id = b.repository_id (r.archived = false)',
      'raw_dim_sonarqube_project sp ON sp.repository_id = r.id',
      'raw_fact_sonarqube_project_quality_latest sql ON sql.project_key = sp.project_key (LEFT JOIN)',
    ],
    sqlFilter:
      "t.archived = false AND prod.archived = false AND r.archived = false -- optional: prod.id = :productId. CTE: DISTINCT ON (t.id, r.id) ORDER BY t.id, r.id, sql.last_analysis_at DESC NULLS LAST. Outer: GROUP BY team_id with COUNT(*), AVG(coverage_pct) FILTER (WHERE coverage_pct IS NOT NULL), COUNT FILTER (WHERE quality_status IN ('PASSED','OK')). Teams with zero repos are filtered via HAVING.",
  },
  asOfDateRule: 'Latest available scan per (team, repository) — the request from/to window is ignored.',
  caveats: [
    "A repository owned by multiple teams under the same product is counted in each team's row. The totals across teams will exceed the portfolio repo count when this happens.",
    'When a teamId filter is set on the request the team breakouts return empty; the consuming panels render accordingly.',
  ],
  consumers: [
    'Coverage by Team panel (Code Quality tab)',
    'QG Pass Rate by Team panel (Code Quality tab)',
  ],
  repositoryMethod: 'getByTeam',
});

const cqFailingQgList = defineQualityConcept({
  conceptKey: 'cq-failing-qg-list',
  displayName: 'Failing Quality Gate List',
  shortDescription: 'In-scope repositories whose latest scan failed the quality gate, sorted most-recently-analysed first.',
  fullDescription: [
    'A list of the repositories that currently sit on the failing side of the Quality Gate Pass Rate tile.',
    '',
    "Inclusion rule: a repository appears here when its latest scan's `quality_status` normalises to `FAILED` (raw values `FAILED` or `ERROR`). Repositories with `quality_status IS NULL` (no verdict recorded) or `PASSED` do not appear.",
    '',
    'Order: most recently analysed first (`last_analysed_at DESC`, nulls last). This is so the panel surfaces the freshly-broken repositories at the top rather than burying them under stale failures.',
    '',
    'The panel surfaces at most the first N rows (typically 5–10 depending on the layout). The full set is computed server-side; the frontend slices for display.',
  ],
  source: snapshotSource(
    "Same in-scope filter as cq-coverage-current. After DISTINCT ON (r.id), the service filters rows where normalised quality_status === 'FAILED' and sorts by lastAnalysedAt DESC NULLS LAST.",
  ),
  asOfDateRule: 'Latest available scan per repository.',
  caveats: [
    "A repository at the top of this list is not necessarily the worst-quality repo — it's the most recently scanned one that happened to fail. A repository with a long-standing failure that hasn't been scanned recently will sit lower or off-screen.",
  ],
  consumers: ['Failing Quality Gate panel (Code Quality tab, distribution column)'],
  repositoryMethod: 'getRepoSnapshot',
});

const cqLowestCoverageList = defineQualityConcept({
  conceptKey: 'cq-lowest-coverage-list',
  displayName: 'Lowest Coverage List',
  shortDescription: 'In-scope repositories sorted by coverage percentage ascending — lowest coverage first.',
  fullDescription: [
    'A ranking of every in-scope repository by its most recent coverage percentage, lowest at the top. The same snapshot read used by the Median Coverage tile, sorted differently.',
    '',
    'Sort: `coverage_pct ASC NULLS LAST`. Repositories with NULL coverage (project scanned but no coverage report) sort to the bottom of the list — they do not occupy the "worst" slot.',
    '',
    'The panel surfaces at most the first N rows. The full set is computed server-side; the frontend slices for display.',
  ],
  source: snapshotSource(
    "Same in-scope filter as cq-coverage-current. After DISTINCT ON (r.id), the service sorts by coveragePct ASC with NULLs sorted last.",
  ),
  asOfDateRule: 'Latest available scan per repository.',
  caveats: [
    "Repositories with NULL coverage (no coverage report configured) sort last and do not look 'worst' in the panel — they look 'unmeasured'. This is intentional but can confuse first-time readers.",
  ],
  consumers: ['Latest coverage — lowest first panel (Code Quality tab, distribution column)'],
  repositoryMethod: 'getRepoSnapshot',
});

const cqWorstReliabilityList = defineQualityConcept({
  conceptKey: 'cq-worst-reliability-list',
  displayName: 'Worst Reliability List',
  shortDescription: 'In-scope repositories at reliability grade C, D, or E, sorted worst-grade first.',
  fullDescription: [
    'A list of repositories that need reliability attention — those at grade C, D, or E at their latest scan. Repositories at grade A or B are not surfaced. See *Reliability Grade Mix* for grade definitions.',
    '',
    'Sort: grade descending (E first, then D, then C), then `last_analysed_at DESC` within each grade. So the top of the list is the most recently analysed E-grade repository, followed by older E-grade repos, then D-grade repos in scan order, and so on.',
    '',
    'The panel surfaces at most the first N rows. The full set is computed server-side; the frontend slices for display.',
  ],
  source: snapshotSource(
    "Same in-scope filter as cq-coverage-current. After DISTINCT ON (r.id), the service filters rows where reliability_grade IN ('C','D','E') and sorts by grade DESC (via internal rank lookup), then lastAnalysedAt DESC NULLS LAST.",
  ),
  asOfDateRule: 'Latest available scan per repository.',
  caveats: [
    'Grade is the per-repository worst-bug-severity verdict, not a bug count (see *Reliability Grade Mix*). A single BLOCKER bug puts a repository at the top of this list even if everything else is clean.',
  ],
  consumers: ['Worst Reliability panel (Code Quality tab, distribution column)'],
  repositoryMethod: 'getRepoSnapshot',
});

const cqFreshness = defineQualityConcept({
  conceptKey: 'cq-freshness',
  displayName: 'Code Quality Freshness',
  shortDescription: 'Newest `last_analysed_at` value across all in-scope repositories — how recent the freshest data point on the tab is.',
  fullDescription: [
    'The most recent code-analysis scan timestamp anywhere in the in-scope cohort.',
    '',
    "Computed as `MAX(last_analysed_at)` over the same snapshot rows that feed the verdict-strip tiles. The footer surfaces this as a relative time ('Updated 2 hours ago') so consumers can judge whether the numbers reflect current state or stale state.",
    '',
    'If the value is days or weeks old, either the SonarCloud sync has stalled or the in-scope repositories are not being actively analysed. Both are worth investigating, but they have different root causes — the Caveat banner above the footer surfaces the sync status separately.',
  ],
  source: snapshotSource(
    'Same in-scope filter as cq-coverage-current. Service computes MAX(lastAnalysedAt) across the resulting rows, returning null when the cohort is empty.',
  ),
  asOfDateRule: 'Latest available scan per repository, then MAX across the cohort.',
  caveats: [
    "A fresh value does not mean every repository was scanned recently — only that at least one was. To see which repositories are stale, sort the snapshot rows directly by lastAnalysedAt ASC (the panels don't expose this directly; use SQL).",
  ],
  consumers: ['Freshness footer (Code Quality tab)'],
  repositoryMethod: 'getRepoSnapshot',
});

const cqSyncCaveats = defineQualityConcept({
  conceptKey: 'cq-sync-caveats',
  displayName: 'Sync Coverage Caveats',
  shortDescription: 'Counts from the most recent SonarCloud sync run indicating how many repository names matched, did not match, or were ambiguous.',
  fullDescription: [
    'A banner at the top of the Code Quality tab surfaces sync-quality counters from the most recent successful run of the SonarCloud ingestion job.',
    '',
    'The pipeline that brings SonarCloud data into the fabric warehouse joins SonarCloud projects to Skystone repositories by name. Three outcomes are possible for each project the pipeline encounters:',
    '',
    '- **Matched** — the SonarCloud project name uniquely matches a known Skystone repository. The project contributes to the in-scope cohort.',
    '- **Unmatched** — the SonarCloud project name does not match any known Skystone repository. The project is invisible to every Code Quality calculation.',
    '- **Ambiguous** — the SonarCloud project name matches multiple Skystone repositories (e.g. forks). The project is excluded to avoid double-counting.',
    '',
    'Non-zero `unmatched` or `ambiguous` counts mean the in-scope cohort is **incomplete** — there are SonarCloud projects whose data is not feeding any tile, panel, or sparkline on the page. Investigating those typically means correcting repository names in either SonarCloud or the Skystone dimension tables until the next sync run picks them up.',
    '',
    "The counters come from `fabric_sync_run` rows where `job_type = 'FABRIC_SONARQUBE_SYNC'` and `status = 'success'`. The banner reads the most recent successful run; if no successful run exists, the banner is hidden.",
  ],
  source: {
    primaryTable: 'fabric_sync_run',
    joins: [],
    sqlFilter:
      "job_type = 'FABRIC_SONARQUBE_SYNC' AND entity_type = 'sonarqube' AND status = 'success' ORDER BY completed_at DESC LIMIT 1 -- counters.repositoryName{Matched,Unmatched,Ambiguous}",
  },
  archivedExcluded: false,
  asOfDateRule: 'Most recent successful sync run.',
  caveats: [
    'The banner shows the LAST SUCCESSFUL sync. If subsequent syncs have failed, the counters can be stale — check the sync run history separately to see whether ingestion is currently healthy.',
    'A repository with an unmatched name is invisible to every Code Quality calculation, not just the banner. The cohort count on other tiles silently excludes it.',
  ],
  consumers: ['Caveat banner (Code Quality tab)'],
  repositoryMethod: 'findLatestSuccessful',
});

/**
 * Ordered list of canonical concept definitions. Order matches the rendering
 * order on the Calculation Reference admin page (logical reading order:
 * WIP → trend → age → aging → open → total → cancelled → throughput → status-count
 * → quality spine (snapshot + trend + by-team + sync caveats) → distribution → freshness).
 */
export const CONCEPTS: ReadonlyArray<ICalculationDefinition> = [
  wipCurrent,
  wipTrend,
  wipAvgAge,
  wipAging,
  open,
  total,
  cancelled,
  throughput,
  statusCount,
  cqCoverageCurrent,
  cqCoverageTrend,
  cqQgPassRate,
  cqReliabilityCurrent,
  cqReliabilityTrend,
  cqMaintainabilityTrend,
  cqByTeam,
  cqSyncCaveats,
  cqFailingQgList,
  cqLowestCoverageList,
  cqWorstReliabilityList,
  cqFreshness,
] as const;
